import React, { useEffect, useMemo, useState } from "react";
import Button from "../components/ui/Button";
import Icon from "../components/ui/Icon";
import Tabs from "../components/ui/Tabs";
import DocumentCard from "../components/ui/DocumentCard";
import Modal from "../components/ui/Modal";
import { InputField, SelectField, TextAreaField } from "../components/ui/Field";

function todayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

function initialUploadState(defaultCategory) {
  return {
    name: "",
    size: "1.0 MB",
    category: defaultCategory,
    date: todayIsoDate(),
    description: "",
  };
}

export function PdfDocumentsPage({ data, onChange, notify, setCurrentPage }) {
  const [activeCategory, setActiveCategory] = useState(data.categories[0].key);
  const [isUploadOpen, setUploadOpen] = useState(false);
  const [formState, setFormState] = useState(() =>
    initialUploadState(data.categories[1]?.key || data.categories[0].key)
  );
  const [formError, setFormError] = useState("");

  useEffect(() => {
      // console.log("Current URL:", window.location.pathname);
      setCurrentPage('/pdfs');
    }, [window.location]);


  const categoryLabelByKey = useMemo(() => {
    const map = {};
    data.categories.forEach((category) => {
      map[category.key] = category.label;
    });
    return map;
  }, [data.categories]);

  const categoryOptions = useMemo(
    () => data.categories.filter((category) => category.key !== "all").map((category) => ({ value: category.key, label: category.label })),
    [data.categories]
  );

  const filteredDocuments = useMemo(() => {
    if (activeCategory === "all") return data.documents;
    return data.documents.filter((document) => document.category === activeCategory);
  }, [activeCategory, data.documents]);

  const tabs = data.categories.map((category) => ({ key: category.key, label: category.label }));

  const openUploadModal = () => {
    setFormState(initialUploadState(categoryOptions[0]?.value || data.categories[0].key));
    setFormError("");
    setUploadOpen(true);
  };

  const closeUploadModal = () => {
    setUploadOpen(false);
  };

  const handleFormChange = (field, value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleUpload = () => {
    const name = formState.name.trim();
    const description = formState.description.trim();

    if (!name) {
      setFormError("Document name is required.");
      return;
    }

    if (!description) {
      setFormError("Please add a short description.");
      return;
    }

    const nextDocument = {
      id: `doc-${Date.now()}`,
      name,
      size: formState.size,
      date: formState.date,
      category: formState.category,
      description,
    };

    onChange?.({ ...data, documents: [nextDocument, ...data.documents] });
    setUploadOpen(false);

    notify?.({
      tone: "success",
      title: "Document uploaded",
      description: `${nextDocument.name} is now available to agents.`,
    });
  };

  const handleView = (document) => {
    notify?.({
      tone: "info",
      title: "Preview opened",
      description: `Viewing ${document.name}.`,
    });
  };

  const handleDownload = (document) => {
    notify?.({
      tone: "success",
      title: "Download started",
      description: `${document.name} is downloading.`,
    });
  };

  const handleDelete = (document) => {
    const nextDocuments = data.documents.filter((entry) => entry.id !== document.id);
    onChange?.({ ...data, documents: nextDocuments });
    notify?.({
      tone: "danger",
      title: "Document deleted",
      description: `${document.name} has been removed.`,
    });
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">PDF Documents</h1>
          <p className="page-subtitle">Manage documents used by your AI agents</p>
        </div>
        <div className="page-actions">
          <Button
            variant="primary"
            iconLeft={<Icon name="upload" size={16} />}
            onClick={openUploadModal}
          >
            Upload PDF
          </Button>
        </div>
      </div>

      <button type="button" className="upload-zone" onClick={openUploadModal}>
        <Icon name="upload" size={52} className="upload-icon" />
        <div className="upload-title">Drop files to upload</div>
        <div className="upload-subtitle">or click to browse files</div>
        <div className="upload-hint">Supports PDF files up to 10MB</div>
      </button>

      <div className="section-gap">
        <Tabs tabs={tabs} activeKey={activeCategory} onChange={setActiveCategory} />
      </div>

      <div className="doc-grid">
        {filteredDocuments.map((document) => (
          <DocumentCard
            key={document.id}
            document={document}
            categoryLabel={categoryLabelByKey[document.category]}
            onView={handleView}
            onDownload={handleDownload}
            onDelete={handleDelete}
          />
        ))}
      </div>

      <Modal
        open={isUploadOpen}
        title="Upload PDF"
        onClose={closeUploadModal}
        actions={(
          <>
            <Button variant="ghost" onClick={closeUploadModal}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleUpload}>
              Upload
            </Button>
          </>
        )}
      >
        {formError && <div className="form-error">{formError}</div>}

        <div className="form-grid">
          <InputField
            label="File Name"
            placeholder="Product Catalog 2026.pdf"
            value={formState.name}
            onChange={(event) => handleFormChange("name", event.target.value)}
          />
          <TextAreaField
            label="Description"
            placeholder="How should agents use this document?"
            rows={3}
            value={formState.description}
            onChange={(event) => handleFormChange("description", event.target.value)}
          />
        </div>

        <div className="form-grid cols-3">
          <InputField
            label="File Size"
            placeholder="1.0 MB"
            value={formState.size}
            onChange={(event) => handleFormChange("size", event.target.value)}
          />
          <SelectField
            label="Category"
            options={categoryOptions}
            value={formState.category}
            onChange={(event) => handleFormChange("category", event.target.value)}
          />
          <InputField
            label="Date"
            type="date"
            value={formState.date}
            onChange={(event) => handleFormChange("date", event.target.value)}
          />
        </div>
      </Modal>
    </div>
  );
}

export default PdfDocumentsPage;